// PestleSwotPanel — Story #6837 Stream 8c (figure 3 = screen-M3-pestle-swot.png).
//
// Left: PESTLE for the primary sector in the SELECTED country (shared, public
// reference data): six cells with a signal pill, [n] source chips (https links only),
// "as at" and a grounding badge. The country switch runs the pestle step FIRST and
// only on `done` changes country; any failure keeps the previous country and shows
// the message. A country with no PESTLE row yet shows "No PESTLE for <country> yet"
// and a Generate button running the same step.
// Right: the invention's SWOT (private, encrypted): 2×2 with "✎ you" on the owner's
// overlay items, a "generated for <country>" badge, the PESTLE/SWOT country notice,
// Edit (putSwotOverlay), Regenerate (pestle → swot → range, force, selected country)
// and a stale banner. insufficient_basis shows the ConfidenceGate.
//
// Not drawn, because nothing supplies them: a separate PESTLE "Refresh" (the API never
// refreshes a PESTLE row younger than 90 days, so the SWOT Regenerate is the only
// refresh), and the "Sources →" link (the sources are listed under the PESTLE).
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Pencil, RefreshCw, X } from 'lucide-react'
import { putSwotOverlay } from '../../services/api'
import { COUNTRY_LABELS, MARKET_COUNTRIES, PESTLE_CELLS, SWOT_QUADRANTS } from '../../types/market'
import type { Project } from '../../types'
import type { MarketPanelProps, MarketSource, PestleCell, SwotOverlayItem, SwotQuadrant } from '../../types/market'
import ConfidenceGate from '../shared/ConfidenceGate'
import { dateLabel } from './marketFormat'
import {
  addOverlayItem, cellSignal, entryText, isOwnerEntry, overlayError, ownerOverlay, removeOverlayItem,
  switchPestleCountry, type CellSignal,
} from './pestleSwotLogic'
import { runMarketChain } from './useMarketAutoRun'
import { invalidateMarket } from './marketQueries'

const CARD = 'bg-white/70 border border-[rgba(12,74,110,0.12)] rounded-xl p-4'
const countryName = (c: string) => (COUNTRY_LABELS as Record<string, string>)[c] ?? c

const CELL_TITLE: Record<PestleCell, string> = {
  political: 'Political', economic: 'Economic', social: 'Social',
  technological: 'Technological', legal: 'Legal', environmental: 'Environmental',
}
const SIGNAL_STYLE: Record<CellSignal, string> = {
  supportive: 'bg-emerald-100 text-emerald-800',
  mixed: 'bg-amber-100 text-amber-800',
  contested: 'bg-orange-100 text-orange-800',
  tightening: 'bg-red-100 text-red-800',
}
const QUADRANT: Record<SwotQuadrant, { title: string; box: string; head: string }> = {
  strengths: { title: 'Strengths', box: 'bg-emerald-50 border-emerald-200', head: 'text-emerald-800' },
  weaknesses: { title: 'Weaknesses', box: 'bg-orange-50 border-orange-200', head: 'text-orange-800' },
  opportunities: { title: 'Opportunities', box: 'bg-sky-50 border-sky-200', head: 'text-sky-800' },
  threats: { title: 'Threats', box: 'bg-red-50 border-red-200', head: 'text-red-800' },
}

function httpMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: unknown } }; message?: string } | null | undefined
  const body = e?.response?.data?.error
  return typeof body === 'string' && body ? body : e?.message || 'The change could not be saved.'
}

function SourceChip({ n, source }: { n: number; source: MarketSource | undefined }) {
  const chip = 'ml-1 rounded border border-sky-300 bg-sky-50 px-1 text-[10px] font-bold text-sky-700'
  if (source && source.url.startsWith('https://')) {
    return <a href={source.url} target="_blank" rel="noopener noreferrer" className={chip} aria-label={`Source ${n}`}>{n}</a>
  }
  return <span className={chip}>{n}</span>
}

export default function PestleSwotPanel({ projectId, market, country, onCountryChange, canRun }: MarketPanelProps) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [draft, setDraft] = useState<SwotOverlayItem[] | null>(null)
  const [typed, setTyped] = useState<Record<SwotQuadrant, string>>({ strengths: '', weaknesses: '', opportunities: '', threats: '' })

  const { pestle, swot } = market
  // ProjectView has already loaded the project under this key; read it, never fetch it here.
  const inventionName = qc.getQueryData<Project>(['project', projectId])?.name
  const gated = market.status === 'insufficient_basis'
  const primaryDetail = market.detail.find((d) => d.role === 'primary')
  const primarySection = market.sectors.find((s) => s.role === 'primary')?.section
  const sectorLabel = primaryDetail?.label ?? (primarySection ? `Section ${primarySection}` : 'this sector')
  const editing = draft !== null

  const afterRun = () => invalidateMarket(qc, projectId)

  const pickCountry = async (c: string) => {
    if (c === country || switching) return
    setMessage(null)
    if (!canRun) {
      onCountryChange(c) // a viewer reads whatever PESTLE already exists for the country
      return
    }
    setSwitching(c)
    const res = await switchPestleCountry(projectId, c)
    setSwitching(null)
    if (res.ok) onCountryChange(c)
    else setMessage(res.message)
  }

  const generatePestle = async () => {
    setBusy(true)
    setMessage(null)
    const res = await switchPestleCountry(projectId, country)
    setBusy(false)
    if (!res.ok) setMessage(res.message.replace(' Still showing the previous country.', ''))
    void afterRun()
  }

  const regenerate = async (force: boolean) => {
    setBusy(true)
    setMessage(null)
    const res = await runMarketChain(projectId, { country, force, classifyFirst: false })
    setBusy(false)
    if (res.phase === 'error') setMessage(res.error)
    else if (res.phase === 'rate_limited') setMessage(`Too many market analyses right now${res.retryAfter != null ? ` — try again in ${res.retryAfter} s` : ''}.`)
    void afterRun()
  }

  const save = useMutation({
    mutationFn: (items: SwotOverlayItem[]) => putSwotOverlay(projectId, items),
    onSuccess: () => {
      setMessage(null)
      setDraft(null)
      return afterRun()
    },
    onError: (err) => setMessage(httpMessage(err)),
  })

  const startEdit = () => { setDraft(ownerOverlay(swot)); setMessage(null) }
  const commitEdit = () => {
    if (!draft) return
    const bad = overlayError(draft)
    if (bad) { setMessage(bad); return }
    save.mutate(draft)
  }
  const addTyped = (q: SwotQuadrant) => {
    if (!draft) return
    const next = addOverlayItem(draft, q, typed[q])
    const bad = overlayError(next)
    if (typed[q].trim().length > 500 || bad) { setMessage(bad ?? 'Each item can be at most 500 characters.'); return }
    setMessage(null)
    setDraft(next)
    setTyped((t) => ({ ...t, [q]: '' }))
  }

  const differs = swot !== null && swot.country !== country
  const sources = pestle?.sources ?? []

  if (gated) {
    return (
      <div data-testid="pestle-swot-panel" className={CARD}>
        <ConfidenceGate
          icon={<AlertTriangle />}
          title="Not enough information for a PESTLE and SWOT"
          description="The documents on this invention do not support a trustworthy sector, so no PESTLE or SWOT was produced — the platform never fabricates an analysis. Add a description or more documents and it runs automatically."
        />
      </div>
    )
  }

  return (
    <div data-testid="pestle-swot-panel" className="space-y-3">
      {message && (
        <div data-testid="pestle-message" role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{message}</div>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {/* ── PESTLE ─────────────────────────────────────────────────── */}
        <div className={CARD} data-testid="pestle-card">
          <h3 className="text-sm font-bold text-iflow-900">PESTLE · {sectorLabel} · {countryName(country)}</h3>
          <p className="mb-2 text-[11px] text-gray-500">
            Sector-level analysis, shared by every invention in this sector
            {pestle && dateLabel(pestle.generated_at) ? <> · as at <strong>{dateLabel(pestle.generated_at)}</strong></> : null}
            {pestle && (
              <span data-testid="pestle-grounding" className="ml-1.5 rounded-full bg-sky-50 px-2 py-0.5 text-[10.5px] font-medium text-sky-700">
                {pestle.grounding === 'web' ? 'web-grounded' : 'official sources only'}
              </span>
            )}
          </p>

          <div className="mb-3 flex flex-wrap gap-1" role="tablist" aria-label="PESTLE country">
            {MARKET_COUNTRIES.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={c === country}
                data-testid={`pestle-country-${c}`}
                disabled={switching !== null}
                onClick={() => pickCountry(c)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium disabled:opacity-60 ${
                  c === country ? 'border-sky-400 bg-sky-50 text-iflow-700' : 'border-[rgba(12,74,110,0.2)] bg-white text-gray-600'
                }`}
              >
                {switching === c ? `${countryName(c)}…` : countryName(c)}
              </button>
            ))}
          </div>

          {pestle === null ? (
            <div data-testid="pestle-empty" className="py-6 text-center">
              <p className="text-xs text-gray-600">No PESTLE for {countryName(country)} yet</p>
              {canRun && (
                <button type="button" data-testid="pestle-generate" disabled={busy} onClick={generatePestle}
                  className="mt-2 inline-flex items-center gap-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Generate
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="pestle-cells">
              {PESTLE_CELLS.map((cell) => {
                const items = pestle.cells[cell] ?? []
                const sig = cellSignal(items)
                return (
                  <div key={cell} data-testid={`pestle-cell-${cell}`} className="rounded-lg border border-[rgba(12,74,110,0.12)] bg-white/80 p-2.5">
                    <div className="mb-1 flex items-center justify-between">
                      <h4 className="text-xs font-bold text-iflow-900">{CELL_TITLE[cell]}</h4>
                      {sig && <span data-testid="cell-signal" className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${SIGNAL_STYLE[sig]}`}>{sig}</span>}
                    </div>
                    <ul className="list-disc space-y-1 pl-4 text-[11.5px] text-gray-700">
                      {items.map((it, i) => (
                        <li key={i}>
                          {it.text}
                          {it.source_idx !== null && it.source_idx !== undefined && sources[it.source_idx] && (
                            <SourceChip n={it.source_idx + 1} source={sources[it.source_idx]} />
                          )}
                        </li>
                      ))}
                      {items.length === 0 && <li className="list-none text-gray-400">Nothing recorded.</li>}
                    </ul>
                  </div>
                )
              })}
            </div>
          )}

          {pestle && (
            <>
              {sources.length > 0 && (
                <ol className="mt-2 space-y-0.5 text-[11px]" data-testid="pestle-sources">
                  {sources.map((s, i) => (
                    <li key={s.url} className="flex gap-1.5">
                      <span className="rounded bg-sky-50 px-1 text-[10px] font-bold text-sky-700">{i + 1}</span>
                      {s.url.startsWith('https://')
                        ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="min-w-0 break-words text-sky-700 underline">{s.title || s.url}</a>
                        : <span>{s.title || 'Source'}</span>}
                    </li>
                  ))}
                </ol>
              )}
              <p className="mt-3 text-[11px] text-gray-500" data-testid="pestle-footer">
                {sources.length > 0 ? `${sources.length} source${sources.length === 1 ? '' : 's'}, each dated` : 'Built from official statistics only — no web sources'}
                {' '}· generated {dateLabel(pestle.generated_at) ?? 'on an unknown date'} · regenerated when older than 90 days or on request ·
                AI-generated analysis, not investment advice.
              </p>
            </>
          )}
        </div>

        {/* ── SWOT ───────────────────────────────────────────────────── */}
        <div className={CARD} data-testid="swot-card">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-iflow-900" data-testid="swot-title">SWOT · {inventionName || 'this invention'}</h3>
              <p className="text-[11px] text-gray-500">
                Invention-specific · reads your documents · private to this invention
                {swot && dateLabel(swot.run_at) ? <> · run {dateLabel(swot.run_at)}</> : null}
              </p>
            </div>
            {canRun && swot && (
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <button type="button" data-testid="swot-save" disabled={save.isPending} onClick={commitEdit} className="rounded-lg bg-sky-500 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Save</button>
                    <button type="button" data-testid="swot-cancel" onClick={() => { setDraft(null); setMessage(null) }} className="rounded-lg border border-[rgba(12,74,110,0.2)] bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600">Cancel</button>
                  </>
                ) : (
                  <>
                    <button type="button" data-testid="swot-edit" disabled={busy} onClick={startEdit} className="flex items-center gap-1 rounded-lg border border-[rgba(12,74,110,0.2)] bg-white px-2.5 py-1.5 text-xs font-medium text-iflow-700 disabled:opacity-50">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button type="button" data-testid="swot-regenerate" disabled={busy} onClick={() => regenerate(true)} className="flex items-center gap-1 rounded-lg border border-[rgba(12,74,110,0.2)] bg-white px-2.5 py-1.5 text-xs font-medium text-iflow-700 disabled:opacity-50">
                      <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Regenerate
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {swot === null ? (
            <div data-testid="swot-empty" className="py-6 text-center">
              <p className="text-xs text-gray-600">No SWOT yet</p>
              {canRun && (
                <button type="button" data-testid="swot-generate" disabled={busy} onClick={() => regenerate(false)}
                  className="mt-2 inline-flex items-center gap-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Generate
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span data-testid="swot-country-badge" className="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700">generated for {countryName(swot.country)}</span>
              </div>

              {swot.stale && (
                <div data-testid="swot-stale" className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  This SWOT is out of date — the primary sector changed since it was generated.
                  {canRun && <> <button type="button" disabled={busy} onClick={() => regenerate(true)} className="font-semibold underline disabled:opacity-50">Regenerate</button></>}
                </div>
              )}

              {differs && (
                <div data-testid="swot-country-notice" className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  PESTLE shown for {countryName(country)}; SWOT is for {countryName(swot.country)}
                  {canRun ? ' — ' : '.'}
                  {canRun && (
                    <button type="button" data-testid="swot-regenerate-country" disabled={busy} onClick={() => regenerate(true)} className="font-semibold underline disabled:opacity-50">
                      Regenerate for {countryName(country)}
                    </button>
                  )}
                </div>
              )}

              <div className="grid gap-2 sm:grid-cols-2" data-testid="swot-grid">
                {SWOT_QUADRANTS.map((q) => (
                  <div key={q} data-testid={`swot-${q}`} className={`rounded-lg border p-2.5 ${QUADRANT[q].box}`}>
                    <h4 className={`mb-1 text-xs font-bold ${QUADRANT[q].head}`}>{QUADRANT[q].title}</h4>
                    <ul className="list-disc space-y-1 pl-4 text-[11.5px] text-gray-800">
                      {swot[q].filter((e) => !(editing && isOwnerEntry(e))).map((e, i) => (
                        <li key={`m${i}`}>
                          {entryText(e)}
                          {isOwnerEntry(e) && <span data-testid="swot-you" className="ml-1.5 rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800">✎ you</span>}
                        </li>
                      ))}
                      {editing && (draft ?? []).filter((d) => d.quadrant === q).map((d) => (
                        <li key={`d${d.text}`} data-testid="swot-draft-item" className="flex items-start justify-between gap-1">
                          <span>{d.text} <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800">✎ you</span></span>
                          <button type="button" aria-label={`Remove ${d.text}`} data-testid="swot-draft-remove" onClick={() => setDraft((cur) => removeOverlayItem(cur ?? [], q, d.text))} className="shrink-0 rounded border border-[rgba(12,74,110,0.2)] bg-white p-0.5 text-gray-600">
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    {editing && (
                      <div className="mt-1.5 flex gap-1">
                        <input
                          aria-label={`Add to ${QUADRANT[q].title}`}
                          data-testid={`swot-input-${q}`}
                          value={typed[q]}
                          onChange={(e) => setTyped((t) => ({ ...t, [q]: e.target.value }))}
                          placeholder="Add your own item"
                          className="min-w-0 flex-1 rounded border border-sky-300 bg-white px-1.5 py-0.5 text-[11px]"
                        />
                        <button type="button" data-testid={`swot-add-${q}`} onClick={() => addTyped(q)} className="rounded bg-sky-500 px-2 py-0.5 text-[11px] font-semibold text-white">Add</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="mt-3 border-t border-[rgba(12,74,110,0.1)] pt-2 text-[11px] text-gray-500">
                Your edits (<span className="rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800">✎ you</span>) are kept as an
                overlay when the analysis regenerates. This analysis reads documents you own and is encrypted with your organisation's key.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
